import { buffer } from '@turf/buffer';
import { point } from '@turf/helpers';
import { twMerge } from 'tailwind-merge';
import { useGeolocation } from '@domain/geolocation/GeolocationContext';
import {
  useIsDesktopScreenWidthOrWider,
  useIsTabletScreenWidthOrWider,
} from '@hooks/useScreenWidth';
import { Button } from '../Clickables/Buttons/Button';
import { ClickableVariant } from '../Clickables/variants';
import { Heading } from '../Heading/Heading';
import { MapIcon } from '../Icons/MapIcon';
import { getStaticMap } from '../Maps/mapData/staticMaps';
import { Modal } from '../Modal/Modal';
import { ModalBody } from '../Modal/ModalBody';
import { NextImageWrapper } from '../NextImage/NextImageWrapper';
import { Text } from '../Text/Text';
import { ReactNode } from 'react';

interface IProps {
  children?: ReactNode;
  className?: string;
  closeButtonVariant?: ClickableVariant;
  isModalOpen: boolean;
  onCloseModal: () => void;
  radius?: number;
  mobileWidth?: string;
  tabletWidth?: string;
  desktopWidth?: string;
}

const DEFAULT_RADIUS = 50; // Default radius in kilometers
const MOBILE_WIDTH = '343px';
const TABLET_WIDTH = '447px';
const DESKTOP_WIDTH = '540px';
const IMAGE_WIDTH = 460;
const IMAGE_HEIGHT = 212;

export const LocationInfoModal = ({
  children,
  className,
  closeButtonVariant = 'controlWhiteBg',
  isModalOpen,
  onCloseModal,
  radius = DEFAULT_RADIUS,
  mobileWidth = MOBILE_WIDTH,
  tabletWidth = TABLET_WIDTH,
  desktopWidth = DESKTOP_WIDTH,
}: IProps) => {
  const isDesktopScreenWidthOrWider = useIsDesktopScreenWidthOrWider();
  const isTabletScreenWidthOrWider = useIsTabletScreenWidthOrWider();

  const width = isDesktopScreenWidthOrWider
    ? desktopWidth
    : isTabletScreenWidthOrWider
      ? tabletWidth
      : mobileWidth;

  const { isLoading: currentLocationLoading, location } = useGeolocation();
  const latitude = location?.coordinates.latitude;
  const longitude = location?.coordinates.longitude;
  const coordsSource = location?.coordsSource;

  const buffered =
    latitude && longitude
      ? buffer(
          point([longitude, latitude], {
            'fill-opacity': 0.5,
            fill: '#E9F8FF',
            stroke: '#0091D3',
            'stroke-width': 1,
          }),
          radius,
          {
            units: 'kilometers',
          },
        )
      : undefined;

  return isModalOpen && !currentLocationLoading ? (
    <Modal
      isOpen
      disableClose
      size="lg"
      closeButtonVariant={closeButtonVariant}
      className={twMerge('mx-6 rounded-lg', className)}
      style={{ width: width }}
    >
      {children ?? (
        <ModalBody className="flex flex-col gap-4">
          {longitude && latitude && (
            <NextImageWrapper
              wrapperClassName="size-full"
              customPlaceholderClassName="rounded-lg"
              imageClassName="size-full object-cover rounded-lg"
              customPlaceholderIcon={<MapIcon />}
              alt="Map of your current location"
              src={getStaticMap({
                longitude: longitude.toString(),
                latitude: latitude.toString(),
                width: IMAGE_WIDTH,
                height: IMAGE_HEIGHT,
                style: 'terrain',
                overlay: encodeURIComponent(JSON.stringify(buffered)),
                zoomLevel: 6,
              })}
              unoptimized
              width={IMAGE_WIDTH}
              height={IMAGE_HEIGHT}
            />
          )}
          <Heading as="p" variant="sm" className="m-0">
            About this location
          </Heading>
          {coordsSource == 'ip' && (
            <>
              <Text variant="md" className="m-0">
                Your area is estimated from your IP address.
              </Text>
              <Text variant="md" className="m-0">
                To get recommendations near your current location, turn on location sharing in your
                browser settings, then refresh the page.
              </Text>
            </>
          )}
          {coordsSource == 'geolocation_api' && (
            <Text variant="md" className="m-0">
              Your area is estimated from your GPS location.
            </Text>
          )}
          <Button onClick={onCloseModal} className="mt-2 w-full md:mt-4">
            Got it!
          </Button>
        </ModalBody>
      )}
    </Modal>
  ) : (
    <></>
  );
};
